import React, { useEffect, useMemo, useState, useContext } from 'react';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import MessagingContext from './Messaging.context';
import useMatchMedia from 'hooks/useMatchMedia';
import { SessionType } from 'models/Message';
import { useParams } from 'react-router-dom';
import api from 'api';
import { getQASide, sentryError } from 'utils';
import { focusElement } from '../../pages/Chat/helpers';

type MessagingProviderType = {
  listingId?: string;
  dealRoomId?: string;
  listingEnquiryId?: string;
  sessionId?: string;
};

const Messaging: React.FC<MessagingProviderType> = (props) => {
  const {
    children,
    listingId,
    dealRoomId,
    listingEnquiryId,
    sessionId: propsSessionId,
  } = props;

  const { sessionId: paramsSessionId } = useParams<{ sessionId: string }>();

  const sessionId = useMemo(
    () => paramsSessionId || propsSessionId,
    [paramsSessionId, propsSessionId],
  );

  const { popServerError } = useContext(PopNotificationsContext);

  const [contactOpen, setContactOpen] = useState<boolean>(false);
  const [contactsOpen, setContactsOpen] = useState<boolean>(false);

  const [loadingSession, setLoadingSession] = useState<boolean>(false);
  const [currentSession, setCurrentSession] = useState<SessionType>();

  const { isBuyer, interlocutor, listing } = currentSession || {};

  const { dataRoomId, dataRoomName, ownerId } = useMemo(() => {
    const { dataRoomId, name, userId } = listing?.dealRoom || {};

    return { dataRoomId, dataRoomName: name, ownerId: userId };
  }, [listing?.dealRoom]);

  const userSide = useMemo(() => getQASide(isBuyer), [isBuyer]);

  const isPhablet = useMatchMedia('isPhablet');

  useEffect(() => {
    setContactOpen(!isPhablet);
    setContactsOpen(!isPhablet);
  }, [isPhablet]);

  useEffect(() => {
    if (!sessionId) return;

    (async () => {
      try {
        setCurrentSession(null);
        setLoadingSession(true);
        const { data } = await api.messages.getSessionById(sessionId);
        setCurrentSession(data);
        focusElement('#message-form-input');
      } catch (e) {
        sentryError(e);
        popServerError(e);
      } finally {
        setLoadingSession(false);
      }
    })();
  }, [popServerError, sessionId]);

  return (
    <MessagingContext.Provider
      value={{
        interlocutor,
        contactOpen,
        contactsOpen,
        loadingSession,
        currentSession,
        listingId,
        dealRoomId,
        listingEnquiryId,
        sessionId,
        ownerId,
        userSide,
        dataRoomId,
        dataRoomName,
        setContactOpen,
        setContactsOpen,
        setCurrentSession,
      }}
    >
      {children}
    </MessagingContext.Provider>
  );
};

export default Messaging;
